import type { U3129EquipmentItem, U3129FindProjectItem, U3129Timesheet } from "../U3129Service";
import type { U3129TimesheetSubprojectSaveOptions } from "./u3129ValidateHonorariaLines";

function equipmentRowIncludeSubProject(row: U3129EquipmentItem, admIncludeSubProjectEnabled: boolean): boolean {
  if (!admIncludeSubProjectEnabled) return false;
  const project = row.project as U3129FindProjectItem | undefined;
  if (!project || !("includeSubProjet" in project)) return false;
  const v = project.includeSubProjet;
  return v === true || (typeof v === "string" && v === "true");
}

export type U3129EquipmentValidationMessages = {
  transactionDate: string;
  project: string;
  subproject: string;
  equipmentType: string;
  quantity: string;
};

function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && !Number.isNaN(v) && Number.isFinite(v);
}

/**
 * Même principe que les dépenses : lignes `equipment` éditables seulement si `status === "Open"`.
 * Clés alignées sur calendrier, autocomplete projet/sous-projet, combo `equipmentTypeId`, quantité entière `quantity`.
 */
export function validateEquipmentLinesForSave(
  data: U3129Timesheet | null | undefined,
  messages: U3129EquipmentValidationMessages,
  options?: U3129TimesheetSubprojectSaveOptions,
): Record<string, string> {
  if (!data || data.status !== "Open") return {};
  const errors: Record<string, string> = {};
  const admIncludeSubProjectEnabled = options?.admIncludeSubProjectEnabled ?? true;
  const list = data.equipment ?? [];
  list.forEach((row, i) => {
    const base = `equipment.${i}`;
    const dateRaw = row.transactionDate;
    if (dateRaw == null || String(dateRaw).trim() === "") {
      errors[`${base}.transactionDate`] = messages.transactionDate;
    }
    const projectId = row.projectId?.trim() || row.project?.id?.trim() || "";
    if (!projectId) {
      errors[`${base}.project.id`] = messages.project;
    }
    if (equipmentRowIncludeSubProject(row, admIncludeSubProjectEnabled)) {
      const sub = row as U3129EquipmentItem & { subproject?: { id?: string } };
      const subId = row.subprojectId?.trim() || sub.subproject?.id?.trim() || "";
      if (!subId) {
        errors[`${base}.subproject.id`] = messages.subproject;
      }
    }
    if (!row.equipmentTypeId?.trim()) {
      errors[`${base}.equipmentTypeId`] = messages.equipmentType;
    }
    if (!isFiniteNumber(row.quantity) || !Number.isInteger(row.quantity)) {
      errors[`${base}.quantity`] = messages.quantity;
    }
  });
  return errors;
}

export function equipmentTabHasValidationErrors(errors: Record<string, string>): boolean {
  return Object.keys(errors).some((k) => k.startsWith("equipment."));
}
